import { useI18NextContext } from '@components/hooks';
import styled from '@emotion/styled';
import { NoSsr } from '@mui/material';
import { mq } from '@styles/breakpoints';
import { SectionSeparator } from '@styles/layout';
import { ThemeProps } from '@theme/base';
import React, { useEffect, useRef, useState } from 'react';
import { FiltersContainer, SearchPadding, SearchResultsContainer, SearchResultsPadding } from '@styles/search-layout';
import { Button, ButtonColor, ButtonSize, ButtonVariant } from '@components/material-ui';
import { withIsServer } from '@components/hoc';
import { ZIndex } from '@styles/z-index';
import { boxShadowMixins } from '@styles/mixins';
import { PageProps } from '@model/pages';
import { withTheme } from '@emotion/react';

export interface SearchLayoutProps extends PageProps {
  searchComponent: JSX.Element;
  noResults: JSX.Element;
  renderFilters: () => any;
  renderMobileFilters: (props?: any) => any;
  canShowSearch: boolean;
  isServer: boolean;
}

interface FilterButtonContainerProps extends ThemeProps {
  isSticky: boolean;
}
export const FilterButtonContainer: any = styled(SearchPadding)(({ theme, isSticky }: FilterButtonContainerProps) => ({
  position: 'sticky',
  top: 0,
  padding: theme.custom.spacing.medium,
  backgroundColor: theme.custom.colors.white,
  zIndex: ZIndex.FILTERS_STICKY_BUTTON,
  transition: `box-shadow ${theme.custom.transitions.default}`,
  boxShadow: isSticky ? boxShadowMixins(theme.custom.colors.group10.base).default : 'none',

  [mq.medium]: {
    display: 'none'
  }
}));

const NonMobileSeparator = styled(SectionSeparator)({
  display: 'none',
  [mq.small]: {
    display: 'block'
  }
});

const SearchLayoutComponent = ({
  searchComponent,
  noResults,
  canShowSearch,
  renderFilters,
  renderMobileFilters,
  isServer
}: SearchLayoutProps) => {
  const t: any = useI18NextContext();

  /* *** STATE *** */
  const [isMobileFiltersButtonSticky, setIsMobileFiltersButtonSticky] = useState<boolean>(false);
  const [showFiltersBarDrawer, setShowFiltersBarDrawer] = useState<boolean>(false);

  const toggleFiltersBarDrawer = (show?: boolean) =>
    setShowFiltersBarDrawer(show === undefined ? !showFiltersBarDrawer : show);

  /* *** HOOKS *** */
  const mobileFilterButtonRef: React.Ref<any> = useRef(null);

  useEffect(() => {
    if (!isServer) {
      // every time isMobileFiltersButtonSticky changes, the old event listener
      // needs to be removed and a new one needs to be created
      window.removeEventListener('scroll', handleMobileFilterResultButton);
      window.addEventListener('scroll', handleMobileFilterResultButton);

      return () => window.removeEventListener('scroll', handleMobileFilterResultButton);
    }
  }, [isMobileFiltersButtonSticky]);

  /* *** LOGIC *** */

  const handleMobileFilterResultButton = () => {
    if (mobileFilterButtonRef.current) {
      const top = mobileFilterButtonRef.current.getBoundingClientRect().top;
      if (top <= 0 && !isMobileFiltersButtonSticky) {
        setIsMobileFiltersButtonSticky(true);
      } else if (top > 0 && isMobileFiltersButtonSticky) {
        setIsMobileFiltersButtonSticky(false);
      }
    }
  };

  return (
    <>
      <FilterButtonContainer ref={mobileFilterButtonRef} isSticky={isMobileFiltersButtonSticky}>
        <Button
          label={t('filters__show-filters--label')}
          variant={ButtonVariant.OUTLINED}
          color={ButtonColor.SECONDARY}
          onClick={() => toggleFiltersBarDrawer(true)}
          size={ButtonSize.SMALL}
        />
      </FilterButtonContainer>
      <NoSsr>{noResults}</NoSsr>
      <NonMobileSeparator size={50} />
      {canShowSearch && (
        <SearchResultsPadding>
          <SearchResultsContainer>{!isServer && searchComponent}</SearchResultsContainer>
          {!isServer && <FiltersContainer>{renderFilters()}</FiltersContainer>}
          <SectionSeparator />
        </SearchResultsPadding>
      )}
      {canShowSearch && renderMobileFilters({ onClose: toggleFiltersBarDrawer, open: showFiltersBarDrawer })}
    </>
  );
};

export const SearchLayout = withTheme(withIsServer(SearchLayoutComponent));
